import { useState, useEffect } from 'react';
import EntityLink from '../components/EntityLink';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import DocumentWallet from '../components/DocumentWallet';
import AuditLog from '../components/AuditLog';
import PurchaseOrderEditDrawer from '../components/PurchaseOrderEditDrawer';

const TABS = ['Details', 'AP Invoices', 'AR Invoices', 'Documents'];

function fmt(n) {
  return '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function PurchaseOrderProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState('Details');
  const [editing, setEditing] = useState(false);

  function load() {
    setError(false);
    api.get(`/purchase-orders/${id}`)
      .then(r => setData(r.data))
      .catch(() => setError(true));
  }
  useEffect(() => { load(); }, [id]);

  if (error) {
    return <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />;
  }
  if (!data) return <StateBlock type="loading" />;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>← Back</button>
          <h1 className="page-title">{data.po_number}</h1>
          <Badge variant={data.status === 'open' ? 'success' : 'neutral'}>{data.status}</Badge>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing(true)}>Edit</button>
      </div>

      <div className="panel-tabs" style={{ marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t} className={'panel-tab' + (tab === t ? ' active' : '')} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Details' && <DetailsTab data={data} />}

      {tab === 'AP Invoices' && <APInvoicesTab apInvoices={data.ap_invoices} />}

      {tab === 'AR Invoices' && <ARInvoicesTab arInvoices={data.ar_invoices} />}

      {tab === 'Documents' && (
        <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
          <DocumentWallet entityType="purchase_order" entityId={data.id} entityName={data.po_number} />
        
      <AuditLog tableName="purchase_orders" recordId={id} /></div>
      )}

      {editing && (
        <PurchaseOrderEditDrawer
          po={data}
          isAdmin={user?.role === 'admin'}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
      <div>{value || '—'}</div>
    </div>
  );
}

function DetailsTab({ data }) {
  const margin = data.sell_rate && data.buy_rate ? (data.sell_rate - data.buy_rate) : null;
  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <h3 style={{ marginTop: 0 }}>Purchase Order Details</h3>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Sell Rate</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{data.sell_rate ? `$${data.sell_rate}/hr` : '—'}</div>
        </div>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Buy Rate</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{data.buy_rate ? `$${data.buy_rate}/hr` : '—'}</div>
        </div>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Margin</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: margin != null ? 'var(--success)' : undefined }}>
            {margin != null ? `$${margin.toFixed(2)}/hr` : '—'}
          </div>
        </div>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Status</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}><Badge variant={data.status === 'open' ? 'success' : 'neutral'}>{data.status}</Badge></div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
        <Field label="Work Order #" value={data.client_wo_number} />
        <Field label="Engagement Type" value={(data.engagement_type || '').replace('_', ' ')} />
        <Field label="Client" value={data.client_name} />
        <Field label="Vendor" value={data.vendor_name} vendorId={data.vendor_id} />
        <Field label="Contractor" value={data.contractor_name} contractorId={data.contractor_id} />
        <Field label="Period" value={data.period_start && data.period_end ? `${data.period_start} – ${data.period_end}` : null} />
        <Field label="Approved Hours" value={data.approved_hours} />
        <Field label="Timesheet Hours" value={data.timesheet_hours} />
      </div>

      {data.notes && (
        <div style={{ marginTop: 16 }}>
          <Field label="Notes" value={data.notes} />
        </div>
      )}
    </div>
  );
}

function APInvoicesTab({ apInvoices }) {
  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <h3 style={{ marginTop: 0 }}>AP Invoices ({(apInvoices || []).length})</h3>
      {(!apInvoices || apInvoices.length === 0) ? (
        <StateBlock type="empty" title="No AP invoices yet" />
      ) : (
        <table className="data-table">
          <thead><tr><th>Invoice #</th><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th><th>Status</th></tr></thead>
          <tbody>
            {apInvoices.map(inv => (
              <tr key={inv.id}>
                <td><EntityLink type="ap_invoice" id={inv.id}>{inv.invoice_number}</EntityLink></td>
                <td>{inv.invoice_date}</td>
                <td style={{ textAlign: 'right' }}>{fmt(inv.amount_to_pay)}</td>
                <td><Badge>{inv.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ARInvoicesTab({ arInvoices }) {
  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <h3 style={{ marginTop: 0 }}>AR Invoices ({(arInvoices || []).length})</h3>
      {(!arInvoices || arInvoices.length === 0) ? (
        <StateBlock type="empty" title="No AR invoices yet for this PO" />
      ) : (
        <table className="data-table">
          <thead><tr><th>Invoice #</th><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th><th>Status</th></tr></thead>
          <tbody>
            {arInvoices.map(inv => (
              <tr key={inv.id}>
                <td><EntityLink type="ar_invoice" id={inv.id}>{inv.invoice_number}</EntityLink></td>
                <td>{inv.invoice_date}</td>
                <td style={{ textAlign: 'right' }}>{fmt(inv.amount)}</td>
                <td><Badge>{inv.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
